/**
 * Stav hrán dielca a upozornenia po úprave (KOLO 135).
 *
 * User na `bok_L` (predvolené hrany 0,5) po zmene na 36 mm: appka
 * potichu nechala 22×0,5, hoci 36 mm doska potrebuje 42 mm pásku —
 * *„proste ide o to, žeby to tam svietilo, že viem, čo mám zlé alebo
 * nahradiť"*. Stav aj texty počíta backend (`services/stav_hran.py`,
 * `dielec_edges.UpozornenieHrany`); tu je len zobrazenie.
 */
import { toast } from "@/components/ui/use-toast";
import type { Dielec } from "@/api/schemas";

/** Hrana je úzka na hrúbku dosky — páska dosku neolepí (chyba); širšia je v poriadku (KOLO 222). */
export const STAV_ZLA_SIRKA = "zla_sirka";
/** Iná hrúbka než predvoľba názvu — hrana existuje (informácia). */
export const STAV_NAHRADENA = "nahradena";
export const STAV_CHYBA = "chyba";

const STRANY = ["x1", "x2", "y1", "y2"] as const;

type SDielcom = Pick<Dielec, "stav_hran">;

/** Strany so zlou šírkou (`["y1", "y2"]`) — tie svietia červeno. */
export function stranySoZlouSirkou(d: SDielcom): string[] {
  const stav = d.stav_hran ?? {};
  return STRANY.filter((s) => stav[s] === STAV_ZLA_SIRKA);
}

/** Strany s náhradnou hrúbkou — len informácia (≠). */
export function stranyNahradene(d: SDielcom): string[] {
  const stav = d.stav_hran ?? {};
  return STRANY.filter((s) => stav[s] === STAV_NAHRADENA);
}

export function maChybuHrany(d: SDielcom): boolean {
  return stranySoZlouSirkou(d).length > 0;
}

/** Text tooltipu ikony „!" pri dielci. */
export function popisStavuHran(d: SDielcom): string {
  const zle = stranySoZlouSirkou(d);
  const nahr = stranyNahradene(d);
  const casti: string[] = [];
  if (zle.length > 0) {
    casti.push(
      `Hrana na ${zle.map((s) => s.toUpperCase()).join(", ")} je úzka ` +
        "na hrúbku dosky — vymeň ju.",
    );
  }
  if (nahr.length > 0) {
    casti.push(
      `Na ${nahr.map((s) => s.toUpperCase()).join(", ")} je iná hrúbka ` +
        "než predvoľba názvu (v potrebnej šírke nebola).",
    );
  }
  return casti.join(" ");
}

/**
 * Toast pre každé upozornenie, ktoré úprava dielca vrátila.
 *
 * Volá sa z hookov úpravy a pridania dielca — jedno miesto, takže
 * upozornenie príde z tabuľky, detailu aj lepenia. Hromadná úprava má
 * vlastný súhrn (`ohlasUpozorneniaHranHromadne`): PATCH volá per dielec
 * a toast za každý by zaplavil obrazovku.
 */
export function ohlasUpozorneniaHran(d: Pick<Dielec, "upozornenia_hran" | "kod">): void {
  for (const u of d.upozornenia_hran ?? []) {
    const chyba = u.druh !== STAV_NAHRADENA;
    toast({
      variant: chyba ? "destructive" : "default",
      title: chyba
        ? `Hrana nesedí — ${d.kod ?? "dielec"}`
        : `Hrana nahradená — ${d.kod ?? "dielec"}`,
      description: u.sprava,
    });
  }
}

type Upozornenie = NonNullable<Dielec["upozornenia_hran"]>[number];

/**
 * Upozornenia z hromadnej úpravy — **jeden** toast za celú akciu (KOLO 169).
 *
 * Do KOLA 169 hromadná úprava odpoveď PATCH zahodila, takže sa user
 * o vymenenej či nesediacej hrane nedozvedel vôbec. Rovnaká veta pri
 * viacerých dielcoch je jeden riadok s ich kódmi („S1_p1, S1_p2: Hrana
 * 0,5 mm v šírke 42 nie je…") — toastov je najviac tri naraz.
 */
export function ohlasUpozorneniaHranHromadne(
  dielce: { kod: string | null; upozornenia: Upozornenie[] }[],
): void {
  const vety = new Map<string, { kody: string[]; chyba: boolean }>();
  for (const d of dielce) {
    for (const u of d.upozornenia) {
      const veta = vety.get(u.sprava) ?? { kody: [], chyba: u.druh !== STAV_NAHRADENA };
      const kod = d.kod ?? "dielec";
      if (!veta.kody.includes(kod)) veta.kody.push(kod);
      vety.set(u.sprava, veta);
    }
  }
  if (vety.size === 0) return;
  const chyba = [...vety.values()].some((v) => v.chyba);
  const pocet = dielce.filter((d) => d.upozornenia.length > 0).length;
  toast({
    variant: chyba ? "destructive" : "default",
    title: chyba ? `Hrany nesedia (${pocet})` : `Hrany nahradené (${pocet})`,
    description: [...vety.entries()]
      .map(([sprava, v]) => `${v.kody.join(", ")}: ${sprava}`)
      .join(" · "),
  });
}
